import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Set } from 'immutable'

// types
import {
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '@aytm/app/swidget/base/questions/Ecommerce/types'

// hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'

// functions
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import { getRemainingTime } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getRemainingTime.ts'
import { getDaysAmount } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getDaysAmount.ts'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import AssetsGate from '@aytm/app/swidget/base/questions/Ecommerce/components/AssetsGate.tsx'
import { LinesSkeleton } from './Skeleton'
import { stripHighlightTags } from '../../utils/stripHighlightTags'
import { unescapeTags } from '@aytm/lib/common/func'

// components
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import AddToCartButton from './AddToCartButton'

// styles
import style from './Cart.module.scss'
import commonStyle from '../Common.module.scss'

export interface ICartProps {
  response: EcommerceResponse
  schema: IEcommerceQuestionSchema
  totals?: number[]
  deliveryDates: number[]
  onAddToCart: (qnt: 1 | -1, index: number) => void
  onCartClose?: () => void
  onItemSelect: (index: number) => void
  onUpdateResponse?: (response: EcommerceResponse) => void
  checkout?: () => void
  scrollTop: () => void
}

export interface ExtendedAnswer extends ArrayElementType<EcommerceResponse['answers']> {
  originalIndex: number
  date: number
}

const Cart = ({
  response,
  schema,
  totals,
  deliveryDates,
  onAddToCart,
  onUpdateResponse,
  onCartClose,
  onItemSelect,
  checkout,
  scrollTop
}: ICartProps) => {
  // custom hooks calls
  const timeline = useTimeline()
  const viewport = useViewport()
  const { t, languageTag } = useSurveyLocale()

  // state
  const [showOptions, setShowOptions] = useState<boolean>(true)
  const [showProducts, setShowProducts] = useState<boolean>(true)

  // refs
  const delivryInfo = useRef({
    remaining: getRemainingTime(),
    num: Math.floor(Math.random() * 90000) + 10000
  })

  // variables
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)
  const na = schema.answers.find(answer => answer.na)
  // The lines and their collapsed thumbs draw the same pictures, so both wait for them behind outlines of
  // the lines; the summary and checkout never wait.
  const lineImages = response.answers
    .map((item, index) => (item.v > 0 ? schema.answers[index].image : null))
    .filter(image => image?.original_url)
    .map(image => getMediumAssetPath(image, 'transparent'))

  // memoized
  const hasDiscountedItems = useMemo(
    () =>
      response.answers.some(
        (answer, index) =>
          answer.v > 0 &&
          schema.answers[index].discounted_price !== 0 &&
          schema.answers[index].price > schema.answers[index].discounted_price
      ),
    [response, schema.answers]
  )
  const productsGroupedByDate = useMemo(() => {
    if (!response?.answers || !deliveryDates) return {}
    const groupedByDate: Record<string, ExtendedAnswer[]> = {}
    response.answers.forEach((answer, index) => {
      if (answer.v <= 0 || answer.id === na?.id) return
      if (!groupedByDate[deliveryDates[index]]) {
        groupedByDate[deliveryDates[index]] = []
      }
      groupedByDate[deliveryDates[index]].push({
        ...answer,
        originalIndex: index,
        date: deliveryDates[index]
      })
    })

    return groupedByDate
  }, [response, deliveryDates, na])

  // effects
  useEffect(() => {
    scrollTop()
  }, [scrollTop])

  // functions
  const calculateSavings = () =>
    response.answers.reduce(
      (acc, answer, index) => {
        if (answer.v <= 0) return acc
        const originalItem = schema.answers[index]
        const regularPrice = originalItem.price * answer.v
        acc.regular += regularPrice
        if (originalItem.discounted_price !== 0 && originalItem.price > originalItem.discounted_price) {
          const discountedPrice = originalItem.discounted_price * answer.v
          acc.saved += regularPrice - discountedPrice
        }
        return acc
      },
      { saved: 0, regular: 0 }
    )

  const removeItem = (index: number) => {
    response.answers[index].v = 0
    timeline.log({
      type: 'delete_from_cart',
      ref: response.answers[index].id
    })
    onUpdateResponse(response)
  }

  const onNaSelect = () => {
    timeline.log({
      type: 'na_select',
      ref: na.id
    })
    const answers = response.answers.map(answer => {
      answer.v = answer.id === na.id ? 1 : 0
      return answer
    })
    response.answers = answers
    onUpdateResponse(response)
  }

  // render functions
  const arrivesBy = () => {
    const timestamps = response.answers
      .map((answer, index) => (answer.v > 0 ? deliveryDates[index] : null))
      .filter(Boolean)

    if (0 === timestamps.length) return ''
    const uniqTimestamps = Set(timestamps)
    if (1 === timestamps.length || 1 === uniqTimestamps.size) {
      return `Arrives ${t<(days: number) => string>(
        'questions',
        'ec',
        'getDaysLeft'
      )(getDaysAmount(timestamps[0]))}, ${new Date(timestamps[0]).toLocaleDateString(languageTag, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      })}`
    }
    const minDate = new Date(Math.min(...timestamps)).toLocaleDateString(languageTag, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })
    const maxDate = new Date(Math.max(...timestamps)).toLocaleDateString(languageTag, {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })
    return `Arrives between ${t<(days: number) => string>(
      'questions',
      'ec',
      'getDaysLeft'
    )(getDaysAmount(Math.min(...timestamps)))}, ${minDate} - ${maxDate}`
  }

  const renderLabels = (schemaAnswer: IEcommerceAnswerSchema) => {
    const isDiscounted = schemaAnswer.discounted_price > 0 && schemaAnswer.price > schemaAnswer.discounted_price
    if (
      !isDiscounted &&
      !schemaAnswer.overal_pick &&
      !schemaAnswer.best_seller &&
      !schemaAnswer.red_label &&
      !schemaAnswer.green_label
    ) {
      return null
    }
    return (
      <div className={commonStyle.labels}>
        {isDiscounted && <div className={commonStyle.rollback}>{t('questions', 'ec', 'walmart', 'rollback')}</div>}
        {schemaAnswer.overal_pick && (
          <div className={commonStyle.bought}>{t('questions', 'ec', 'walmart', 'popularPick')}</div>
        )}
        {schemaAnswer.best_seller && (
          <div className={commonStyle.best}>{t('questions', 'ec', 'walmart', 'bestSeller')}</div>
        )}
        {schemaAnswer.red_label && <div className={commonStyle.redLabel}>{unescapeTags(schemaAnswer.red_label)}</div>}
        {schemaAnswer.green_label && (
          <div className={commonStyle.greenLabel}>{stripHighlightTags(unescapeTags(schemaAnswer.green_label))}</div>
        )}
      </div>
    )
  }

  const renderError = () => {
    if (!valid) {
      return (
        <div
          className={style.error}
          dangerouslySetInnerHTML={{
            __html:
              (schema.max_money_spent && totals[1] > schema.max_money_spent
                ? t<(max: string) => string>(
                    'questions',
                    'ec',
                    'maxSpentError'
                  )(formatPriceWithLocale(schema.max_money_spent, schema.price_mask, languageTag))
                : '') +
              (schema.max_cart_size && totals[0] > schema.max_cart_size
                ? t<(max: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size)
                : '')
          }}
        />
      )
    }
    return null
  }

  return (
    <>
      <div className={`${style.cart} ${viewport.mobile ? style.cartMobile : ''}`} data-testid="SW-EC-Cart">
        {!viewport.mobile && (
          <button className={commonStyle.back} onClick={onCartClose}>
            <i className="wm-ChevronLeft" />
            {t('questions', 'ec', 'walmart', 'back')}
          </button>
        )}

        <div className={style.count}>
          <span className={style.text}>{t('questions', 'ec', 'walmart', 'cart')}</span>
          <span
            className={style.amount}
            dangerouslySetInnerHTML={{
              __html: `(${t<(amount: number) => string>('questions', 'ec', 'walmart', 'items')(totals[0])})`
            }}
          ></span>
        </div>
        {0 === totals[0] ? (
          <div className={style.empty}>
            <div className={style.image}></div>
            <div className={style.note}>{t('questions', 'ec', 'walmart', 'cartEmpty')}</div>
            <button className={style.continue} onClick={onCartClose}>
              {t('questions', 'ec', 'walmart', 'continue')}
            </button>
            {na && (
              <button className={style.naButton} onClick={onNaSelect}>
                {na.text || t('questions', 'ec', 'na')}
              </button>
            )}
          </div>
        ) : (
          <div className={style.wrapper}>
            <div className={style.items}>
              <div className={style.delivery}>
                <div className={style.deliveryHeader} onClick={() => setShowOptions(!showOptions)}>
                  <div className={style.image}></div>
                  <div className={style.text}>{t('questions', 'ec', 'walmart', 'options')}</div>
                  {showOptions ? <i className="wm-ChevronUp"></i> : <i className="wm-ChevronDown"></i>}
                </div>
                {showOptions && (
                  <EmptyLink color="#0053e2">
                    <div className={style.deliveryContent}>
                      <div className={style.options}>
                        <div className={`${style.image} ${style.option1}`}></div>
                        <div className={style.text}>{t('questions', 'ec', 'walmart', 'shipping')}</div>
                        <div className={style.smallText}>{t('questions', 'ec', 'walmart', 'itemsAvailible')}</div>
                      </div>
                      <div className={`${style.options} ${style.optionsDisabled}`}>
                        <div className={`${style.image} ${style.option2}`}></div>
                        <div className={style.text}>{t('questions', 'ec', 'walmart', 'pickup')}</div>
                        <div className={style.smallText}>{t('questions', 'ec', 'walmart', 'notAvailable')}</div>
                      </div>
                      <div className={`${style.options} ${style.optionsDisabled}`}>
                        <div className={`${style.image} ${style.option3}`}></div>
                        <div className={style.text}>{t('questions', 'ec', 'walmart', 'delivery')}</div>
                        <div className={style.smallText}>{t('questions', 'ec', 'walmart', 'notAvailable')}</div>
                      </div>
                    </div>
                  </EmptyLink>
                )}
              </div>
              <div className={style.content}>
                <div className={style.shipping}>
                  <div className={style.image}></div>
                  <div className={style.text}>
                    <div className={style.date} dangerouslySetInnerHTML={{ __html: arrivesBy() }} />
                    <div className={style.remain}>
                      {t('questions', 'ec', 'walmart', 'orderWithin')}
                      &nbsp;
                      <strong>{delivryInfo.current.remaining}</strong>
                    </div>
                    <EmptyLink color="#0053e2">
                      <div className={style.num}>{delivryInfo.current.num}</div>
                    </EmptyLink>
                  </div>
                </div>
                {!viewport.mobile && (
                  <div className={style.productHead}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t<(amount: number) => string>('questions', 'ec', 'walmart', 'items')(totals[0])
                      }}
                    />
                    <span onClick={() => setShowProducts(!showProducts)}>
                      {showProducts ? <i className="wm-ChevronUp" /> : <i className="wm-ChevronDown" />}
                    </span>
                  </div>
                )}

                <AssetsGate urls={lineImages} loader={<LinesSkeleton count={lineImages.length} />}>
                  {showProducts ? (
                    <div className={style.products}>
                      <div>
                        {Object.entries(productsGroupedByDate).map(([date, answers]) => {
                          const totalItems = answers.reduce((sum, item) => sum + item.v, 0)
                          return (
                            <div key={date} className={style.groupWrapper}>
                              <div className={style.arrives}>
                                <div
                                  className={style.cardDate}
                                  dangerouslySetInnerHTML={{
                                    __html: `${t('questions', 'ec', 'walmart', 'arrivesBy')} ${new Date(
                                      Number(date)
                                    ).toLocaleDateString(languageTag, {
                                      weekday: 'short',
                                      month: 'short',
                                      day: 'numeric'
                                    })}`
                                  }}
                                />
                                <div
                                  className={style.cardItems}
                                  dangerouslySetInnerHTML={{
                                    __html: t<(amount: number) => string>(
                                      'questions',
                                      'ec',
                                      'walmart',
                                      'items'
                                    )(totalItems)
                                  }}
                                />
                              </div>
                              {answers.map((item, index) => (
                                <div className={style.product} key={index} data-testid="SW-EC-CartItem">
                                  <span className={style.freeShipping}>
                                    {t('questions', 'ec', 'walmart', 'freeShipping')}
                                  </span>
                                  {renderLabels(schema.answers[item.originalIndex])}
                                  <div className={style.productInner}>
                                    {schema.answers[item.originalIndex].image &&
                                    schema.answers[item.originalIndex].image.original_url ? (
                                      <img
                                        onClick={() => onItemSelect(item.originalIndex)}
                                        className={style.image}
                                        alt={
                                          schema.answers[item.originalIndex].image?.alt_text ||
                                          t('questions', 'img', 'noAlt')
                                        }
                                        src={getMediumAssetPath(
                                          schema.answers[item.originalIndex].image,
                                          'transparent'
                                        )}
                                      />
                                    ) : (
                                      <div className={style.noImage} onClick={() => onItemSelect(item.originalIndex)} />
                                    )}
                                    <div className={style.info}>
                                      <span
                                        onClick={() => onItemSelect(item.originalIndex)}
                                        className={style.text}
                                        dangerouslySetInnerHTML={{ __html: schema.answers[item.originalIndex].text }}
                                      />
                                      <div className={style.returns}>
                                        <div className={style.returnImage}></div>
                                        <span className={style.returnText}>
                                          {t('questions', 'ec', 'walmart', 'free90days')}
                                        </span>
                                      </div>
                                    </div>
                                    <div className={style.price}>
                                      {schema.answers[item.originalIndex].discounted_price &&
                                      schema.answers[item.originalIndex].discounted_price !=
                                        schema.answers[item.originalIndex].price ? (
                                        <>
                                          <div
                                            className={style.discountedPrice}
                                            dangerouslySetInnerHTML={{
                                              __html: formatPriceWithLocale(
                                                item.v * schema.answers[item.originalIndex].discounted_price,
                                                schema.price_mask,
                                                languageTag,
                                                true,
                                                false
                                              )
                                            }}
                                          />
                                          <div
                                            className={style.lastPrice}
                                            dangerouslySetInnerHTML={{
                                              __html: formatPriceWithLocale(
                                                item.v * schema.answers[item.originalIndex].price,
                                                schema.price_mask,
                                                languageTag,
                                                true,
                                                false
                                              )
                                            }}
                                          />
                                          <div className={style.save}>
                                            <div className={style.text}>
                                              {t('questions', 'ec', 'walmart', 'youSave')}
                                            </div>
                                            <div
                                              className={style.saved}
                                              dangerouslySetInnerHTML={{
                                                __html: formatPriceWithLocale(
                                                  item.v * schema.answers[item.originalIndex].price -
                                                    item.v * schema.answers[item.originalIndex].discounted_price,
                                                  schema.price_mask,
                                                  languageTag,
                                                  true,
                                                  false
                                                )
                                              }}
                                            />
                                          </div>
                                        </>
                                      ) : (
                                        <div
                                          className={style.defaultPrice}
                                          dangerouslySetInnerHTML={{
                                            __html: formatPriceWithLocale(
                                              item.v * response.answers[item.originalIndex].price,
                                              schema.price_mask,
                                              languageTag,
                                              true,
                                              false
                                            )
                                          }}
                                        />
                                      )}
                                    </div>
                                  </div>
                                  <div className={style.controls}>
                                    <button
                                      className={style.controlsButton}
                                      onClick={() => removeItem(item.originalIndex)}
                                    >
                                      {t('questions', 'ec', 'walmart', 'remove')}
                                    </button>
                                    <EmptyLink color="#0053e2">
                                      <button className={style.controlsButton}>
                                        {t('questions', 'ec', 'walmart', 'saveForLater')}
                                      </button>
                                    </EmptyLink>
                                    <AddToCartButton
                                      amount={item.v}
                                      index={item.originalIndex}
                                      onAddToCart={onAddToCart}
                                      limit={schema.max_qty_per_product}
                                      defaultLabel={t('questions', 'ec', 'walmart', 'add')}
                                      showIcon={true}
                                      collapsible={false}
                                      findability={false}
                                      width="120px"
                                      skin="transparent"
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className={style.productsPreview}>
                      {response.answers.map((item, index) => (
                        <>
                          {item.v > 0 ? (
                            <div className={style.preview} key={index} onClick={() => setShowProducts(true)}>
                              {item.v > 1 && <div className={style.counter}>{item.v}</div>}
                              {schema.answers[index].image && schema.answers[index].image.original_url ? (
                                <img
                                  className={style.image}
                                  alt={schema.answers[index].image?.alt_text || t('questions', 'img', 'noAlt')}
                                  src={getMediumAssetPath(schema.answers[index].image, 'transparent')}
                                />
                              ) : (
                                <div className={style.noImage} />
                              )}
                            </div>
                          ) : null}
                        </>
                      ))}
                    </div>
                  )}
                </AssetsGate>
                {viewport.mobile && (
                  <div className={style.productHead} onClick={() => setShowProducts(!showProducts)}>
                    <span>
                      {showProducts
                        ? t('questions', 'ec', 'walmart', 'hideDetails')
                        : t('questions', 'ec', 'walmart', 'showDetails')}
                    </span>
                    <span>{showProducts ? <i className="wm-ChevronUp" /> : <i className="wm-ChevronDown" />}</span>
                  </div>
                )}
              </div>
            </div>
            {!viewport.mobile && (
              <div className={style.process}>
                <div className={style.checkout}>
                  <button className={`${style.checkoutButton} ${!valid ? style.disabled : ''}`} onClick={checkout}>
                    {t('questions', 'ec', 'walmart', 'checkout')}
                  </button>
                  <EmptyLink color="#0053e2">
                    <div className={style.sign}>
                      {t('questions', 'ec', 'walmart', 'experience')}&nbsp;
                      <span>{t('questions', 'ec', 'walmart', 'signIn')}</span>
                    </div>
                  </EmptyLink>
                  {renderError()}
                  <div className={style.compute}>
                    {hasDiscountedItems ? (
                      <>
                        <div className={style.row}>
                          <div className={style.title}>
                            <strong>{t('questions', 'ec', 'walmart', 'subtotal')}&nbsp;</strong>
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t<(amount: number) => string>('questions', 'ec', 'walmart', 'items')(totals[0])
                              }}
                            />
                          </div>
                          <div
                            className={style.lastPrice}
                            dangerouslySetInnerHTML={{
                              __html: `${formatPriceWithLocale(calculateSavings().regular, schema.price_mask, languageTag, true, false)}`
                            }}
                          ></div>
                        </div>
                        {calculateSavings().saved > 0 && (
                          <div className={`${style.row} ${style.rowSavings}`}>
                            <div className={style.title}>
                              <strong>{t('questions', 'ec', 'walmart', 'savings')}</strong>
                            </div>
                            <div
                              className={style.savings}
                              dangerouslySetInnerHTML={{
                                __html: `-${formatPriceWithLocale(calculateSavings().saved, schema.price_mask, languageTag, true, false)}`
                              }}
                            ></div>
                          </div>
                        )}
                        <div className={style.totals}>
                          <div
                            className={style.discounted}
                            dangerouslySetInnerHTML={{
                              __html: `${formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)}`
                            }}
                          ></div>
                        </div>
                      </>
                    ) : (
                      <div className={style.row}>
                        <div className={style.title}>
                          <strong>{t('questions', 'ec', 'walmart', 'subtotal')}</strong>
                          <span>
                            &nbsp;({t<(amount: number) => string>('questions', 'ec', 'walmart', 'items')(totals[0])})
                          </span>
                        </div>
                        <div
                          className={style.value}
                          dangerouslySetInnerHTML={{
                            __html: `${formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)}`
                          }}
                        ></div>
                      </div>
                    )}
                    <div className={style.row}>
                      <div className={style.titleSmall}>{t('questions', 'ec', 'walmart', 'shipping')}</div>
                      <div className={style.green}>{t('questions', 'ec', 'walmart', 'free')}</div>
                    </div>
                    <div className={style.row}>
                      <div className={style.title}>
                        <strong>{t('questions', 'ec', 'walmart', 'taxes')}</strong>
                      </div>
                      <div className={style.value}>{t('questions', 'ec', 'walmart', 'calcAndCheck')}</div>
                    </div>
                    <div className={`${style.row} ${style.lastRow}`}>
                      <div className={style.title}>
                        <strong>{t('questions', 'ec', 'walmart', 'estimate')}</strong>
                      </div>
                      <div className={style.value}>
                        <strong
                          dangerouslySetInnerHTML={{
                            __html: `${formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)}`
                          }}
                        ></strong>
                      </div>
                    </div>
                    <div className={style.affirm}>
                      <div className={style.image}></div>
                      <div className={style.text}>{t('questions', 'ec', 'walmart', 'monthlyPayments')}</div>
                      <EmptyLink color="#0053e2">
                        <div className={style.continue}>{t('questions', 'ec', 'walmart', 'withAffirm')}</div>
                      </EmptyLink>
                    </div>
                  </div>
                </div>
                <div className={style.gift}>
                  <div className={style.info}>
                    <EmptyLink color="#0053e2">
                      <div className={style.checkbox}></div>
                    </EmptyLink>
                    <div className={style.text}>{t('questions', 'ec', 'walmart', 'isGift')}</div>
                    <EmptyLink color="#0053e2">
                      <i className="wm-InfoCircle" />
                    </EmptyLink>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {!viewport.mobile && (
          <footer className={style.footer}>
            <div className={style.policy}>{t('questions', 'ec', 'walmart', 'rightsReserved')}</div>
            <div className={style.links}>
              <EmptyLink color="#0053e2">
                <div className={style.link}>{t('questions', 'ec', 'walmart', 'giveFeedback')}</div>
              </EmptyLink>
              <EmptyLink color="#0053e2">
                <div className={style.link}>{t('questions', 'ec', 'walmart', 'caPrivacy')}</div>
              </EmptyLink>
              <EmptyLink color="#0053e2">
                <div className={style.link}>
                  <div className={style.image} />
                  {t('questions', 'ec', 'walmart', 'privacyChoice')}
                </div>
              </EmptyLink>
              <EmptyLink color="#0053e2">
                <div className={style.link}>{t('questions', 'ec', 'walmart', 'noticeCollection')}</div>
              </EmptyLink>
              <EmptyLink color="#0053e2">
                <div className={style.link}>{t('questions', 'ec', 'walmart', 'requestPersonal')}</div>
              </EmptyLink>
              <EmptyLink color="#0053e2">
                <div className={style.link}>{t('questions', 'ec', 'walmart', 'deleteAccount')}</div>
              </EmptyLink>
              <EmptyLink color="#0053e2">
                <div className={style.link}>{t('questions', 'ec', 'walmart', 'supplyChains')}</div>
              </EmptyLink>
            </div>
          </footer>
        )}
      </div>
      {totals[0] > 0 && viewport.mobile && (
        <div className={style.checkoutFooter}>
          <div className={style.checkoutTop}>
            <span className={style.text}>{t('questions', 'ec', 'walmart', 'estimate')}</span>
            {hasDiscountedItems ? (
              <div className={style.prices}>
                <span
                  className={style.lastPrice}
                  dangerouslySetInnerHTML={{
                    __html: `${formatPriceWithLocale(calculateSavings().regular, schema.price_mask, languageTag, true, false)}`
                  }}
                />
                <span
                  className={style.discounted}
                  dangerouslySetInnerHTML={{
                    __html: `${formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)}`
                  }}
                />
              </div>
            ) : (
              <span
                className={style.regular}
                dangerouslySetInnerHTML={{
                  __html: `${formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)}`
                }}
              />
            )}
          </div>
          {renderError()}
          <button
            className={`${style.continue} ${!valid ? style.disabled : ''} ${na ? style.small : style.full}`}
            onClick={checkout}
          >
            {t('questions', 'ec', 'walmart', 'checkout')}
          </button>
        </div>
      )}
    </>
  )
}

export default Cart
